import axios, { AxiosInstance } from 'axios';
import { Integrador, Instancia } from './store';

const API_BASE_URL = import.meta.env.VITE_MANAGEMENT_API_URL || '/api';

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials:true,
  timeout:15000,
  headers: {
    'Content-Type': 'application/json',
  },
});
api.interceptors.request.use(config => {
  const token = localStorage.getItem('accessToken');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing:Promise<any>|null=null;
api.interceptors.response.use(response=>response,async error=>{
 const original=error.config;
 const skip=['/auth/login','/auth/refresh','/auth/logout','/auth/request-password-reset','/auth/reset-password'].includes(original?.url);
 if(localStorage.getItem('managementLoggedOut')==='1'||error.response?.status!==401||!original||original._retry||skip)return Promise.reject(error);
 original._retry=true;
 try{if(!refreshing)refreshing=api.post('/auth/refresh').then(({data})=>{localStorage.setItem('accessToken',data.accessToken);localStorage.setItem('user',JSON.stringify(data.user));window.dispatchEvent(new Event('management-auth-changed'));return data;}).finally(()=>{refreshing=null;});await refreshing;return api(original);}
 catch(refreshError){window.dispatchEvent(new Event('management-auth-expired'));return Promise.reject(refreshError);}
});

// Integradores
export const integradoresAPI = {
  listar: () => api.get<{ total: number; data: Integrador[] }>('/integradores'),
  obter: (id: string) => api.get<Integrador>(`/integradores/${id}`),
  criar: (data: Pick<Integrador, 'nome'|'email'|'empresa'|'alias'> & {subscription:{revision:number;planCode:string;status:'active'|'trial';endsAt:string|null;reason:string}}) =>
    api.post<{ integrador: Integrador; instancia: Instancia; message: string }>(
      '/integradores',
      data,
      {timeout:30000}
    ),
  atualizar: (id: string, data: Partial<Integrador>) =>
    api.patch<Integrador>(`/integradores/${id}`, data),
  deletar: (id: string) => api.delete<{ message: string }>(`/integradores/${id}`),
  atualizarInstancia: (id: string) => 
    api.post<{ message: string; deployments: any[] }>(`/integradores/${id}/atualizar`),
  obterInfo: (id: string) => 
    api.get<{ pods: any[]; deployments: any[] }>(`/integradores/${id}/info`),
};

// Instâncias
export const instanciasAPI = {
  supportAccess: (id: string) => api.get<{ email: string; exists: boolean; active: boolean; canManage: boolean; reason?: string }>(`/instancias/${id}/support-access`),
  createSupportAccess: (id: string) => api.post<{ message: string }>(`/instancias/${id}/support-access`),
  removeSupportAccess: (id: string) => api.delete<{ message: string }>(`/instancias/${id}/support-access`),
  listar: () => api.get<{ total: number; data: Instancia[] }>('/instancias'),
  obter: (id: string) => api.get<Instancia>(`/instancias/${id}`),
  escalar: (id: string, data: { replicas_frontend?: number; replicas_backend?: number }) =>
    api.post<{ message: string; instancia: Instancia }>(`/instancias/${id}/escalar`, data),
  reiniciar: (id: string) =>
    api.post<{ message: string; instancia: Instancia }>(`/instancias/${id}/reiniciar`),
  recreate: (id: string) =>
    api.post<{ message: string; result: any; instancia: Instancia }>(`/instancias/${id}/recreate`),
  k8sInfo: () => api.get<Record<string, any>>('/instancias/k8s-info'),
  logs: (id: string, container?: string, lines?: number, sinceSeconds?: number) =>
    api.get<{ instancia: string; namespace: string; container: string; lines: number; sinceSeconds: number | null; logs: string }>(
      `/instancias/${id}/logs`, { params: { container, lines, sinceSeconds } }
    ),
};

// Faturamento
export const faturamentoAPI = {
  listar: () => api.get<{ total: number; data: any[] }>('/faturamento'),
  obter: (integrador_id: string) => api.get<any>(`/faturamento/${integrador_id}`),
  processar: (integrador_id: string) =>
    api.post<{ status: string; valor: number; data_pagamento: string }>(
      `/faturamento/${integrador_id}/processar`
    ),
};

// Infraestrutura (solar-shared)
type TriggerJobOptions = {
  days?: number;
  dates?: string[];
};

export const infraAPI = {
  obterInfo: () => api.get<any>('/infra'),
  restart: (deployment: string) =>
    api.post<{ deployment: string; message: string; timestamp: string }>(`/infra/restart/${deployment}`),
  deploy: (deployment: string) =>
    api.post<{ deployment: string; message: string; timestamp: string }>(`/infra/deploy/${deployment}`),
  logs: (deployment: string, lines?: number, sinceSeconds?: number) =>
    api.get<{ deployment: string; lines: number; sinceSeconds: number | null; logs: string }>(
      `/infra/logs/${deployment}`, { params: { lines, sinceSeconds } }
    ),
  triggerJob: (jobName: string, alias?: string, options?: TriggerJobOptions) =>
    api.post<{ success: boolean; message: string }>('/infra/trigger-job', { jobName, alias, options }),
};

export default api;
